import React, { useEffect, useMemo, useState } from "react";
import "./reports.scss";
import { getLeadReport } from "../../api/crawlerApi";
import { formatProjectValue } from "../../utils/text";

function ScoreBadge({ value }) {
  const className =
    value >= 80 ? "score score--high" : value >= 60 ? "score score--medium" : "score score--low";

  return <span className={className}>{value}</span>;
}

export default function ReportsPage() {
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    source: "",
    state: "",
  });
  const [report, setReport] = useState({ summary: {}, top_high_score_opportunities: [] });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    setLoading(true);
    getLeadReport(filters)
      .then((data) => {
        if (!active) return;
        setReport(data);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || "Failed to load report.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [filters]);

  const kpis = useMemo(() => {
    const summary = report.summary || {};

    return [
      { label: "Total Leads", value: summary.total_leads ?? 0, tone: "red" },
      { label: "New Leads", value: summary.new_leads ?? 0, tone: "green" },
      { label: "Synced Leads", value: summary.synced_leads ?? 0, tone: "orange" },
      { label: "Average Lead Score", value: summary.average_lead_score ?? 0, tone: "purple" },
    ];
  }, [report.summary]);

  return (
    <div className="reports-page">
      <div className="reports-page__topbar">
        <div>
          <h2>Reports</h2>
          <p>Monitor lead performance and top-scoring opportunities.</p>
        </div>
      </div>

      <div className="reports-filters">
        <input
          type="date"
          value={filters.date_from}
          onChange={(event) => setFilters((prev) => ({ ...prev, date_from: event.target.value }))}
        />
        <input
          type="date"
          value={filters.date_to}
          onChange={(event) => setFilters((prev) => ({ ...prev, date_to: event.target.value }))}
        />
        <input
          placeholder="Source"
          value={filters.source}
          onChange={(event) => setFilters((prev) => ({ ...prev, source: event.target.value }))}
        />
        <input
          placeholder="State"
          value={filters.state}
          onChange={(event) => setFilters((prev) => ({ ...prev, state: event.target.value }))}
        />
      </div>

      {error && <div className="report-card">{error}</div>}

      <div className="kpi-grid">
        {kpis.map((kpi) => (
          <div className={`kpi-card kpi-card--${kpi.tone}`} key={kpi.label}>
            <span>{kpi.label}</span>
            <strong>{loading ? "..." : Number(kpi.value).toLocaleString()}</strong>
          </div>
        ))}
      </div>

      <div className="tables-grid">
        <div className="report-card">
          <div className="report-card__header">
            <h3>Top High-Score Opportunities</h3>
          </div>

          <div className="report-table-wrapper">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>State</th>
                  <th>Value</th>
                  <th>Source</th>
                  <th>Lead Score</th>
                </tr>
              </thead>
              <tbody>
                {(report.top_high_score_opportunities || []).length === 0 ? (
                  <tr>
                    <td colSpan="5">{loading ? "Loading..." : "No records found."}</td>
                  </tr>
                ) : (
                  (report.top_high_score_opportunities || []).map((item) => (
                    <tr key={item.id}>
                      <td><strong>{item.project_name}</strong></td>
                      <td>{item.state || "-"}</td>
                      <td>{formatProjectValue(item.project_value_amount)}</td>
                      <td>{item.source || "-"}</td>
                      <td><ScoreBadge value={Number(item.lead_score || 0)} /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
